import { useLayoutEffect, useRef, type RefObject } from "react";

// FLIP row-slide animation, the React port of the legacy flipTableRows /
// flipReorder: when the row order changes (season change or a sort), each row
// slides from its old position to its new one instead of snapping. Rows with no
// prior position just appear in place (first render).
//
// Usage: give the rows container a ref, mark each row with `data-flip-key`, and
// pass a `dep` that changes when the order changes (e.g. the joined key list).
export function useFlipRows(
  containerRef: RefObject<HTMLElement | null>,
  dep: unknown
): void {
  const prev = useRef<Map<string, number>>(new Map());

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const rows = container.querySelectorAll<HTMLElement>("[data-flip-key]");
    const before = prev.current;
    const next = new Map<string, number>();

    rows.forEach((row) => {
      const key = row.dataset.flipKey;
      if (key === undefined) return;
      const top = row.getBoundingClientRect().top;
      next.set(key, top);

      const old = before.get(key);
      if (old === undefined) return; // new row → no slide
      const delta = old - top;
      if (Math.abs(delta) < 1) return;

      // Invert to the old spot with no transition, force a reflow, then play to
      // the new spot — the standard FLIP, matching the legacy 0.35s ease.
      row.style.transition = "none";
      row.style.transform = `translateY(${delta}px)`;
      row.getBoundingClientRect();
      row.style.transition = "transform 0.35s ease";
      row.style.transform = "translateY(0)";
      const clear = () => {
        row.style.transition = "";
        row.style.transform = "";
        row.removeEventListener("transitionend", clear);
      };
      row.addEventListener("transitionend", clear);
    });

    prev.current = next;
  }, [containerRef, dep]);
}
